// TestPlanDesignerPOC.js — Documentation Fonctionnelle : POC « Plans de Test de Performance » (module Performances).
// Aperçu INTERACTIVE et fidèle à la DISPOSITION de l'écran réel de conception d'un plan de test de charge :
// en-tête module (nom du plan + statut + barre de progression Brouillon→Prêt→En cours→Terminé + KPIs), formulaire
// de configuration du plan (Nom / Description / Durée totale), onglets Timeline / Seuils / Environnements, table des
// entrées (scénarios) et cartes de seuils (alerte/critique). Les actions sont SIMULÉES localement (aucun appel réseau).
// Source de vérité : automata-studio-frontend [PRF][01]-TestPlanDesigner.js + automata_performances TestPlanController.
import React, { useState } from 'react';
import {
  Plus, Trash2, Edit, Target, Clock, Users, Gauge, Timer, AlertTriangle,
  BarChart2, Save, Server, CheckCircle, XCircle, Info, ArrowRight, ArrowLeft,
  Layers, Settings, RefreshCw, Link2, Unlink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// 10 types de métriques surveillables (libellés + unités exacts de MetricType côté backend).
const METRIC_TYPES = [
  { value: 'CPU_USAGE', label: 'Utilisation CPU', icon: 'Cpu', unit: '%' },
  { value: 'MEMORY_USAGE', label: 'Utilisation Mémoire', icon: 'HardDrive', unit: '%' },
  { value: 'DISK_USAGE', label: 'Utilisation Disque', icon: 'HardDrive', unit: '%' },
  { value: 'NETWORK_IO', label: 'E/S Réseau', icon: 'Wifi', unit: 'MB/s' },
  { value: 'DB_SESSIONS', label: 'Sessions DB', icon: 'Database', unit: '' },
  { value: 'DB_LOCKS', label: 'Verrous DB', icon: 'Database', unit: '' },
  { value: 'PROCESS_COUNT', label: 'Nb Processus', icon: 'Activity', unit: '' },
  { value: 'RESPONSE_TIME', label: 'Temps de Réponse', icon: 'Timer', unit: 'ms' },
  { value: 'THROUGHPUT', label: 'Débit', icon: 'TrendingUp', unit: 'req/s' },
  { value: 'ERROR_RATE', label: "Taux d'Erreur", icon: 'AlertTriangle', unit: '%' },
];
const metricOf = (v) => METRIC_TYPES.find((m) => m.value === v) || { label: v, icon: 'Gauge', unit: '' };

// Palette des entrées (couleurs de timeline de l'écran réel).
const ENTRY_COLORS = ['#8B5CF6', '#6366F1', '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];

// 4 statuts du cycle de vie (TestPlanStatus) — ordre = barre de progression.
const STATUS_FLOW = ['DRAFT', 'READY', 'IN_PROGRESS', 'COMPLETED'];
const STATUS = {
  DRAFT: { label: 'Brouillon', color: '#6B7280' },
  READY: { label: 'Prêt', color: '#3B82F6' },
  IN_PROGRESS: { label: 'En cours', color: '#F59E0B' },
  COMPLETED: { label: 'Terminé', color: '#10B981' },
};

// Données en dur du plan en cours d'édition.
const SAMPLE_PLAN = {
  id: 42,
  name: 'Clôture journalière — montée en charge',
  description: 'Pic de charge sur la fenêtre de clôture comptable',
  totalDurationMinutes: 60,
  status: 'DRAFT',
  createdDate: '2026-06-10',
};
// Entrées (scénarios) : tâche rejouée par N utilisateurs, avec offset de début, durée et ramp-up.
const SAMPLE_ENTRIES = [
  { id: 1, taskName: 'Connexion Amplitude', startOffsetMinutes: 0, durationMinutes: 15, concurrentUsers: 50, rampUpSeconds: 30, color: '#8B5CF6' },
  { id: 2, taskName: 'Saisie virement SEPA', startOffsetMinutes: 10, durationMinutes: 30, concurrentUsers: 120, rampUpSeconds: 60, color: '#3B82F6' },
  { id: 3, taskName: 'Consultation solde', startOffsetMinutes: 20, durationMinutes: 20, concurrentUsers: 200, rampUpSeconds: 45, color: '#10B981' },
];
// Seuils : alerte (warning) + critique (critical) par métrique.
const SAMPLE_THRESHOLDS = [
  { id: 1, metricType: 'CPU_USAGE', warningValue: 70, criticalValue: 90 },
  { id: 2, metricType: 'RESPONSE_TIME', warningValue: 1500, criticalValue: 3000 },
  { id: 3, metricType: 'ERROR_RATE', warningValue: 1, criticalValue: 5 },
];
// Environnements cibles : liés / disponibles.
const SAMPLE_ENVIRONMENTS = [
  { id: 1, name: 'PROD Amplitude', appType: 'Genero GDC', linked: true },
  { id: 2, name: 'RECETTE CBS', appType: 'Genero GAS', linked: true },
  { id: 3, name: 'INTÉGRATION DB', appType: 'Oracle', linked: false },
  { id: 4, name: 'BAC À SABLE', appType: 'API REST', linked: false },
];

const EMPTY_ENTRY = { taskName: '', concurrentUsers: '', durationMinutes: '', startOffsetMinutes: '', rampUpSeconds: '' };
const EMPTY_THRESHOLD = { metricType: 'CPU_USAGE', warningValue: '', criticalValue: '' };

// ---- Petits sous-composants de la réplique ---------------------------------
const MetricIcon = ({ metricType, box = 'h-9 w-9', size = 'h-4 w-4' }) => {
  const m = metricOf(metricType);
  return (
    <span className={`${box} rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br from-violet-500 to-purple-600`}>
      <Icon name={m.icon} className={`${size} text-white`} />
    </span>
  );
};

// Barre de progression du cycle de vie du plan (réplique de PlanProgressBar).
const PlanProgressBar = ({ status }) => {
  const idx = STATUS_FLOW.indexOf(status);
  return (
    <div className="flex items-center gap-1.5">
      {STATUS_FLOW.map((s, i) => (
        <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= idx ? 'bg-white' : 'bg-white/25'}`} />
      ))}
    </div>
  );
};

// Carte KPI de l'en-tête (réplique de KPICard).
const KpiCard = ({ icon, label, value }) => (
  <div className="bg-white/10 rounded-xl px-3 py-2 flex items-center gap-2">
    <Icon name={icon} className="h-4 w-4 text-white/80" />
    <div>
      <p className="text-lg font-bold text-white leading-none">{value}</p>
      <p className="text-[10px] text-white/60">{label}</p>
    </div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const TestPlanDesignerPOC = () => {
  const [plan, setPlan] = useState(SAMPLE_PLAN);
  const [entries, setEntries] = useState(SAMPLE_ENTRIES);
  const [thresholds, setThresholds] = useState(SAMPLE_THRESHOLDS);
  const [environments, setEnvironments] = useState(SAMPLE_ENVIRONMENTS);
  const [tab, setTab] = useState('timeline'); // 'timeline' | 'thresholds' | 'environments'
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY);
  const [thresholdForm, setThresholdForm] = useState(EMPTY_THRESHOLD);
  const [metricSearch, setMetricSearch] = useState('');
  const [flash, setFlash] = useState(''); // message éphémère de confirmation

  const totalUsers = entries.reduce((s, e) => s + (parseInt(e.concurrentUsers, 10) || 0), 0);
  const linkedEnvs = environments.filter((e) => e.linked);
  const st = STATUS[plan.status] || STATUS.DRAFT;

  const notify = (msg) => { setFlash(msg); window.setTimeout(() => setFlash(''), 1800); };

  const setPlanField = (k) => (e) => setPlan((p) => ({ ...p, [k]: e.target.value }));
  const setEntry = (k) => (e) => setEntryForm((f) => ({ ...f, [k]: e.target.value }));
  const setThr = (k) => (e) => setThresholdForm((f) => ({ ...f, [k]: e.target.value }));

  const addEntry = () => {
    if (!entryForm.taskName.trim() || !entryForm.concurrentUsers) return;
    const id = Math.max(0, ...entries.map((e) => e.id)) + 1;
    setEntries((prev) => [...prev, {
      id,
      taskName: entryForm.taskName.trim(),
      startOffsetMinutes: parseInt(entryForm.startOffsetMinutes, 10) || 0,
      durationMinutes: parseInt(entryForm.durationMinutes, 10) || 10,
      concurrentUsers: parseInt(entryForm.concurrentUsers, 10) || 1,
      rampUpSeconds: parseInt(entryForm.rampUpSeconds, 10) || 30,
      color: ENTRY_COLORS[prev.length % ENTRY_COLORS.length],
    }]);
    setEntryForm(EMPTY_ENTRY);
    notify('Entrée ajoutée au plan (simulation)');
  };
  const removeEntry = (id) => { setEntries((prev) => prev.filter((e) => e.id !== id)); notify('Entrée retirée'); };

  const addThreshold = () => {
    const w = parseFloat(thresholdForm.warningValue);
    const c = parseFloat(thresholdForm.criticalValue);
    if (isNaN(w) || isNaN(c) || w >= c) return;
    const id = Math.max(0, ...thresholds.map((t) => t.id)) + 1;
    setThresholds((prev) => [...prev, { id, metricType: thresholdForm.metricType, warningValue: w, criticalValue: c }]);
    setThresholdForm(EMPTY_THRESHOLD);
    notify('Seuil ajouté au plan (simulation)');
  };
  const removeThreshold = (id) => { setThresholds((prev) => prev.filter((t) => t.id !== id)); notify('Seuil retiré'); };

  const toggleEnv = (id) => {
    setEnvironments((prev) => prev.map((e) => e.id === id ? { ...e, linked: !e.linked } : e));
    notify('Liaison environnement mise à jour (simulation)');
  };

  const filteredMetrics = METRIC_TYPES.filter((m) => m.label.toLowerCase().includes(metricSearch.toLowerCase()));

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Plans de Test de Performance</h1>
          <Badge color="#EF4444">Module Performances</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Plans de Test de Performance</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== (2) Aperçu fidèle de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête module : retour + nom + statut + barre de progression + KPIs */}
        <div className="px-5 py-4 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="p-2 rounded-xl bg-white/10"><ArrowLeft className="h-5 w-5" /></span>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold leading-tight">{plan.name || 'Plan de test'}</h2>
                  <Badge color={st.color}><span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: st.color }} />{st.label}</Badge>
                </div>
                <p className="text-xs text-white/60 mt-0.5">ID : {plan.id} · Créé le {new Date(plan.createdDate).toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => notify('Données rechargées (simulation)')} className="bg-white/10 hover:bg-white/20 text-white" size="sm"><RefreshCw className="h-4 w-4" /></Button>
              <Button onClick={() => notify('Plan enregistré (simulation)')} className="bg-white text-violet-700 hover:bg-white/90" size="sm"><Save className="h-4 w-4" />Enregistrer</Button>
              <Button onClick={() => entries.length ? (setPlan((p) => ({ ...p, status: 'READY' })), notify('Plan marqué Prêt (simulation)')) : notify('Ajoutez au moins une entrée')} className="bg-emerald-500 hover:bg-emerald-600 text-white" size="sm"><CheckCircle className="h-4 w-4" />Marquer Prêt</Button>
            </div>
          </div>

          {/* Barre de progression du cycle de vie */}
          <div className="mt-3"><PlanProgressBar status={plan.status} /></div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <KpiCard icon="Layers" label="Entrées" value={entries.length} />
            <KpiCard icon="Gauge" label="Seuils" value={thresholds.length} />
            <KpiCard icon="Server" label="Environnements" value={linkedEnvs.length} />
            <KpiCard icon="Clock" label="Durée (min)" value={plan.totalDurationMinutes} />
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* Message éphémère */}
          {flash && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm">
              <CheckCircle className="h-4 w-4" />{flash}
            </div>
          )}

          {/* Formulaire de configuration du plan : Nom / Description / Durée */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2"><Settings className="h-4 w-4" />Configuration du Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1">Nom du plan <span className="text-red-500">*</span></span>
                <input value={plan.name} onChange={setPlanField('name')} placeholder="Ex : Montée en charge clôture" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
              </label>
              <label className="text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1">Description</span>
                <input value={plan.description} onChange={setPlanField('description')} placeholder="Objectif du test" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
              </label>
              <label className="text-sm">
                <span className="block text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Durée totale (minutes) <span className="text-red-500">*</span></span>
                <input type="number" min="1" max="1440" value={plan.totalDurationMinutes} onChange={setPlanField('totalDurationMinutes')} placeholder="60" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
              </label>
            </div>
          </div>

          {/* Onglets Timeline / Seuils / Environnements */}
          <Tabs
            tabs={[
              { id: 'timeline', label: 'Timeline', count: entries.length },
              { id: 'thresholds', label: 'Seuils', count: thresholds.length },
              { id: 'environments', label: 'Environnements', count: linkedEnvs.length },
            ]}
            active={tab}
            onChange={setTab}
          />

          {/* ----- Onglet Timeline (entrées / scénarios) ----- */}
          {tab === 'timeline' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Chronologie du Test</h3>
                <span className="text-xs text-gray-400 dark:text-gray-500">{entries.length} entrée(s) · {totalUsers} utilisateurs cumulés · durée totale {plan.totalDurationMinutes} min</span>
              </div>

              {/* Ligne d'ajout d'une entrée */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-3 items-end p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Target className="h-3.5 w-3.5" />Tâche</span>
                  <input value={entryForm.taskName} onChange={setEntry('taskName')} placeholder="Ex : Saisie virement SEPA" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Début (min)</span>
                  <input type="number" min="0" value={entryForm.startOffsetMinutes} onChange={setEntry('startOffsetMinutes')} placeholder="0" className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Durée (min)</span>
                  <input type="number" min="1" value={entryForm.durationMinutes} onChange={setEntry('durationMinutes')} placeholder="10" className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Users className="h-3.5 w-3.5" />Utilisateurs</span>
                  <input type="number" min="1" value={entryForm.concurrentUsers} onChange={setEntry('concurrentUsers')} placeholder="100" className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Timer className="h-3.5 w-3.5" />Ramp-up (s)</span>
                  <input type="number" min="0" value={entryForm.rampUpSeconds} onChange={setEntry('rampUpSeconds')} placeholder="30" className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                </label>
                <Button onClick={addEntry} disabled={!entryForm.taskName.trim() || !entryForm.concurrentUsers} className="bg-violet-600 hover:bg-violet-700 text-white"><Plus className="h-4 w-4" />Ajouter</Button>
              </div>

              {/* Table « Détails des entrées » : # / Tâche / Début / Durée / Utilisateurs / Ramp-up / Actions */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    <tr>{['#', 'Tâche', 'Début', 'Durée', 'Utilisateurs', 'Ramp-up', 'Actions'].map((h) => <th key={h} className={`font-medium uppercase tracking-wider text-xs px-4 py-2.5 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                    {[...entries].sort((a, b) => (a.startOffsetMinutes || 0) - (b.startOffsetMinutes || 0)).map((e, idx) => (
                      <tr key={e.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2.5"><span className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: e.color }} /><span className="text-xs text-gray-400">{idx + 1}</span></span></td>
                        <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{e.taskName}</td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{e.startOffsetMinutes} min</td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{e.durationMinutes} min</td>
                        <td className="px-4 py-2.5"><span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300"><Users className="h-3 w-3" />{e.concurrentUsers}</span></td>
                        <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{e.rampUpSeconds}s</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className="inline-flex items-center justify-end gap-1">
                            <button onClick={() => notify('Édition de l\'entrée (simulation)')} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/30" title="Modifier"><Edit className="h-3.5 w-3.5" /></button>
                            <button onClick={() => removeEntry(e.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Retirer"><Trash2 className="h-3.5 w-3.5" /></button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {entries.length === 0 && <EmptyState icon={Layers} title="Aucune entrée" message="Ajoutez des tâches à votre plan pour construire le scénario de charge." />}
              </div>
            </div>
          )}

          {/* ----- Onglet Seuils (alerte / critique par métrique) ----- */}
          {tab === 'thresholds' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Seuils de Performance</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Définissez les seuils d'alerte et critiques pour chaque métrique.</p>
                </div>
              </div>

              {/* Ligne d'ajout d'un seuil : métrique + valeur d'alerte + valeur critique */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Gauge className="h-3.5 w-3.5" />Type de métrique</span>
                  <select value={thresholdForm.metricType} onChange={setThr('metricType')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none">
                    {METRIC_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}{m.unit ? ` (${m.unit})` : ''}</option>)}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-amber-600 dark:text-amber-400 mb-1 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Valeur d'alerte</span>
                  <input type="number" value={thresholdForm.warningValue} onChange={setThr('warningValue')} placeholder="70" className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-amber-500 focus:outline-none" />
                </label>
                <label className="text-sm">
                  <span className="block text-red-600 dark:text-red-400 mb-1 flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />Valeur critique</span>
                  <input type="number" value={thresholdForm.criticalValue} onChange={setThr('criticalValue')} placeholder="90" className="w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-red-500 focus:outline-none" />
                </label>
                <Button onClick={addThreshold} disabled={thresholdForm.warningValue === '' || thresholdForm.criticalValue === ''} className="bg-violet-600 hover:bg-violet-700 text-white"><Plus className="h-4 w-4" />Ajouter</Button>
              </div>

              {/* Cartes de seuils (réplique des cartes à jauge de l'écran réel) */}
              {thresholds.length === 0 ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700"><EmptyState icon={Gauge} title="Aucun seuil défini" message="Définissez une métrique avec ses valeurs d'alerte et critique." /></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {thresholds.map((t) => {
                    const m = metricOf(t.metricType);
                    return (
                      <div key={t.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-800">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <MetricIcon metricType={t.metricType} box="h-10 w-10" size="h-5 w-5" />
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{m.label}</h4>
                              {m.unit && <span className="text-[10px] text-gray-400 dark:text-gray-500">Unité : {m.unit}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => notify('Édition du seuil (simulation)')} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="Modifier"><Edit className="h-3.5 w-3.5" /></button>
                            <button onClick={() => removeThreshold(t.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700" title="Retirer"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                        {/* Barre de zones normal / alerte / critique */}
                        <div className="flex h-2.5 rounded-full overflow-hidden mb-2">
                          <span className="bg-emerald-500" style={{ flex: t.warningValue }} title="Normal" />
                          <span className="bg-amber-500" style={{ flex: Math.max(t.criticalValue - t.warningValue, 1) }} title="Alerte" />
                          <span className="bg-red-500" style={{ flex: 1 }} title="Critique" />
                        </div>
                        <div className="flex items-center justify-around text-[11px]">
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-gray-500 dark:text-gray-400">Normal</span></span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /><span className="text-amber-600 dark:text-amber-400 font-medium">{t.warningValue}{m.unit}</span></span>
                          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /><span className="text-red-600 dark:text-red-400 font-medium">{t.criticalValue}{m.unit}</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ----- Onglet Environnements (liés / disponibles) ----- */}
          {tab === 'environments' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Environnements liés</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Associez les environnements cibles pour l'exécution de ce plan de test.</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Associés */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"><Link2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" /></span>
                    <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">Associés ({linkedEnvs.length})</h4>
                  </div>
                  <div className="space-y-2">
                    {linkedEnvs.length === 0 ? (
                      <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-8 text-center text-xs text-gray-400 dark:text-gray-500"><Server className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />Aucun environnement associé</div>
                    ) : linkedEnvs.map((env) => (
                      <div key={env.id} className="rounded-xl border border-emerald-200 dark:border-emerald-800/30 bg-emerald-50/50 dark:bg-emerald-900/10 p-4 flex items-start gap-3">
                        <span className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0"><Server className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{env.name}</p>
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md">{env.appType}</span>
                        </div>
                        <button onClick={() => toggleEnv(env.id)} className="p-2 rounded-xl text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30" title="Délier"><Unlink className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Disponibles */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"><Server className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" /></span>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Disponibles ({environments.filter((e) => !e.linked).length})</h4>
                  </div>
                  <div className="space-y-2">
                    {environments.filter((e) => !e.linked).length === 0 ? (
                      <div className="rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 p-8 text-center text-xs text-gray-400 dark:text-gray-500"><CheckCircle className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />Tous les environnements sont associés</div>
                    ) : environments.filter((e) => !e.linked).map((env) => (
                      <div key={env.id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex items-start gap-3">
                        <span className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center shrink-0"><Server className="h-5 w-5 text-gray-500 dark:text-gray-400" /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{env.name}</p>
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-md">{env.appType}</span>
                        </div>
                        <button onClick={() => toggleEnv(env.id)} className="p-2 rounded-xl text-gray-400 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/30" title="Lier"><Link2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-blue-500" />1. Configurer le plan</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Renseigner un <strong>Nom</strong> de plan parlant. <button onClick={() => setPlan((p) => ({ ...p, name: 'Nouveau plan de charge' }))} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Préciser une <strong>Description</strong> et la <strong>Durée totale</strong> (minutes). <button onClick={() => setPlan((p) => ({ ...p, totalDurationMinutes: 90 }))} className="text-blue-600 dark:text-blue-400 underline">Mettre 90 min</button></GuideStep>
              <GuideStep n={3}>Le plan démarre en <strong>Brouillon</strong> ; il passe <strong>Prêt</strong> dès qu'il contient une entrée.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Users className="h-5 w-5 text-blue-500" />2. Ajouter des entrées</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Onglet <strong>Timeline</strong> → choisir une <strong>tâche</strong> à rejouer. <button onClick={() => { setTab('timeline'); setEntryForm({ taskName: 'Recherche client', startOffsetMinutes: '5', durationMinutes: '20', concurrentUsers: '150', rampUpSeconds: '60' }); }} className="text-blue-600 dark:text-blue-400 underline">Pré-remplir</button></GuideStep>
              <GuideStep n={2}>Définir le <strong>début</strong>, la <strong>durée</strong>, les <strong>utilisateurs concurrents</strong> et le <strong>ramp-up</strong>.</GuideStep>
              <GuideStep n={3}>Cliquer sur <strong>Ajouter</strong> ; chaque entrée reçoit une couleur de timeline. <button onClick={() => { setTab('timeline'); addEntry(); }} className="text-blue-600 dark:text-blue-400 underline">Ajouter</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Gauge className="h-5 w-5 text-blue-500" />3. Définir des seuils</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Onglet <strong>Seuils</strong> → choisir une <strong>métrique</strong> (CPU, temps de réponse…). <button onClick={() => { setTab('thresholds'); setThresholdForm({ metricType: 'MEMORY_USAGE', warningValue: '75', criticalValue: '90' }); }} className="text-blue-600 dark:text-blue-400 underline">Pré-remplir</button></GuideStep>
              <GuideStep n={2}>Saisir la <strong>valeur d'alerte</strong> puis la <strong>valeur critique</strong> (alerte &lt; critique).</GuideStep>
              <GuideStep n={3}>Cliquer sur <strong>Ajouter</strong> ; le seuil qualifiera l'exécution. <button onClick={() => { setTab('thresholds'); addThreshold(); }} className="text-blue-600 dark:text-blue-400 underline">Ajouter</button></GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Référence : métriques disponibles ===== */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><BarChart2 className="h-5 w-5 text-violet-500" />Métriques disponibles (MetricType)</CardTitle></CardHeader>
        <CardContent>
          <SearchBar value={metricSearch} onChange={setMetricSearch} placeholder="Filtrer les métriques…" className="mb-3 max-w-sm" />
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Métrique', 'Code', 'Unité'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {filteredMetrics.map((m) => (
                  <tr key={m.value} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2"><span className="flex items-center gap-2"><MetricIcon metricType={m.value} box="h-7 w-7" />{m.label}</span></td>
                    <td className="px-4 py-2 font-mono text-gray-500 dark:text-gray-400">{m.value}</td>
                    <td className="px-4 py-2">{m.unit ? <Badge>{m.unit}</Badge> : <span className="text-gray-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredMetrics.length === 0 && <EmptyState icon={Gauge} title="Aucune métrique" message="Aucune métrique ne correspond au filtre." />}
          </div>
        </CardContent>
      </Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Info className="h-5 w-5 text-blue-500" />Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>nom</strong> et la <strong>durée totale</strong> (en minutes) du plan sont obligatoires.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Chaque entrée porte une <strong>tâche</strong>, un <strong>début</strong>, une <strong>durée</strong>, des <strong>utilisateurs concurrents</strong> (≥ 1) et un <strong>ramp-up</strong>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Un seuil combine <strong>métrique + valeur d'alerte + valeur critique</strong> (alerte &lt; critique).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un plan passe <strong>Prêt</strong> seulement s'il contient au moins une entrée.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Cycle de vie : <strong>Brouillon → Prêt → En cours → Terminé</strong> (édition verrouillée hors Brouillon).</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/performance/test-plans'],
              ['POST', '#3B82F6', '/api/automata/performance/test-plans'],
              ['PUT', '#F59E0B', '/api/automata/performance/test-plans/{id}'],
              ['PATCH', '#8B5CF6', '/api/automata/performance/test-plans/{id}/status'],
              ['POST', '#3B82F6', '/api/automata/performance/test-plans/{id}/entries'],
              ['POST', '#3B82F6', '/api/automata/performance/test-plans/{id}/thresholds'],
              ['POST', '#10B981', '/api/automata/performance/executions/test-plans/{id}/start'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_performances / TestPlanController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default TestPlanDesignerPOC;
